import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { Paginator } from '@/models/globalModels';
import { FilterObj, TableIdEnum, TableOptions } from '@/models/tableModels';
import { getTableContent, getTableOptions } from '@/services/api';

const tablesApi = createApi({
  reducerPath: 'tablesApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/' }),
  tagTypes: ['tables'],

  endpoints: (builder) => ({
    getTableContent: builder.query<
      Paginator<any>,
      { key: TableIdEnum; filter?: FilterObj; httpMethod?: 'get' | 'post'; customEndpoint?: string; queryFilters?: Record<string, string> }
    >({
      queryFn: async ({ key, filter, httpMethod, customEndpoint, queryFilters }) => {
        try {
          const data = await getTableContent(key, filter, httpMethod, customEndpoint, queryFilters);
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (_r, _e, arg) => [{ type: 'tables', id: arg.key }],
    }),

    getTableOptions: builder.query<TableOptions, { key: TableIdEnum; httpMethod?: 'get' | 'post'; customEndpoint?: string }>({
      queryFn: async ({ key, httpMethod, customEndpoint }) => {
        try {
          const data = await getTableOptions(key, httpMethod, customEndpoint);
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (_r, _e, key) => [{ type: 'tables', id: `options-${key}` }],
    }),
  }),
});

export default tablesApi;

export const useLazyGetTableContentQuery = <TData>() => {
  const [getTableContent, { data: tableData, ...result }] = tablesApi.useLazyGetTableContentQuery();

  return [getTableContent, { data: tableData as Paginator<TData> | undefined, ...result }] as const;
};

export const useGetTableContentQuery = <TData>(...args: Parameters<typeof tablesApi.useGetTableContentQuery>) => {
  const { data: tableData, isLoading, isError, ...result } = tablesApi.useGetTableContentQuery(...args);

  return {
    data: tableData as Paginator<TData> | undefined,
    isLoading: isLoading as boolean,
    isError: isError as boolean,
    ...result,
  } as const;
};

export const { useGetTableOptionsQuery, useLazyGetTableOptionsQuery } = tablesApi;
